import { useState, useCallback } from 'react';
import useDetectClickOutside from '../../../hooks/useDetectClickOutside';
const focusableElementsSelector = 'button:not([tabindex="-1"]):not([disabled]), input:not([tabindex="-1"]):not([disabled])';
const getFocusable = (el) => {
    if (!el) {
        return [];
    }
    return Array.from(el.querySelectorAll(focusableElementsSelector) ?? []);
};
const useSelect = (selectList, selectContainer, select, isSearch = false) => {
    const [show, setShow] = useState(false);
    useDetectClickOutside(selectContainer, () => {
        setShow(false);
    });
    const selectFirstElementInList = useCallback((focusableItems) => {
        if (focusableItems.length > 0) {
            focusableItems[0].focus();
        }
    }, []);
    const closeSelect = useCallback(() => {
        select.current?.focus();
        setShow(false);
    }, [select, setShow]);
    const onKeyDownSelectItem = useCallback((e) => {
        const focusable = selectList?.current ? getFocusable(selectList.current) : [];
        const focusedItem = focusable.find((el) => el === document.activeElement);
        const numberOfFocusableItems = focusable.length;
        const focusedItemIndex = focusedItem ? focusable.indexOf(focusedItem) : 0;
        const isOnFristElement = focusedItemIndex === 0;
        const isOnLastElement = focusedItemIndex === numberOfFocusableItems - 1;
        const lastElementIndex = numberOfFocusableItems - 1;
        switch (e.key) {
            case 'Down':
            case 'ArrowDown':
                e.stopPropagation();
                e.preventDefault();
                if (isOnLastElement) {
                    if (isSearch && select?.current) {
                        select.current.focus();
                        return;
                    }
                    focusable[0].focus();
                    return;
                }
                focusable[focusedItemIndex + 1].focus();
                break;
            case 'Up':
            case 'ArrowUp':
                e.preventDefault();
                if (isOnFristElement) {
                    if (isSearch && select?.current) {
                        select.current.focus();
                        return;
                    }
                    focusable[lastElementIndex].focus();
                }
                else {
                    focusable[focusedItemIndex - 1].focus();
                }
                break;
            case 'Esc':
            case 'Escape':
                closeSelect();
                break;
            case 'Tab':
            case 'Enter':
                e.preventDefault();
                closeSelect();
                break;
            default:
                break;
        }
    }, [closeSelect, isSearch, select, selectList]);
    const onKeyDownSelect = useCallback((e) => {
        const focusable = selectList?.current ? getFocusable(selectList.current) : [];
        switch (e.key) {
            case 'Down':
            case 'ArrowDown':
                e.stopPropagation();
                e.preventDefault();
                selectFirstElementInList(focusable);
                setShow(true);
                break;
            case 'Tab':
                closeSelect();
                break;
            case 'ArrowUp':
            case 'Up':
                e.preventDefault();
                closeSelect();
                break;
            default:
                break;
        }
    }, [selectList, closeSelect, selectFirstElementInList]);
    const onKeyDownSearchInput = useCallback((e) => {
        const focusable = selectList?.current ? getFocusable(selectList.current) : [];
        switch (e.key) {
            case 'Down':
            case 'ArrowDown':
                e.stopPropagation();
                selectFirstElementInList(focusable);
                setShow(true);
                break;
            case 'Esc':
            case 'Escape':
                closeSelect();
                break;
            case 'Enter':
                e.preventDefault();
                break;
            case 'Tab':
                closeSelect();
                break;
            default:
                break;
        }
    }, [setShow, selectList, closeSelect, selectFirstElementInList]);
    return { show, setShow, onKeyDownSelect, onKeyDownSelectItem, onKeyDownSearchInput, closeSelect };
};
export default useSelect;
//# sourceMappingURL=data:application/json;base64,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